import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { cn } from '@/app/utils/style'
import type { ContentTable } from '@/sanity.types'

// Solid white keeps scrolled cells from showing through; the pseudo-element restores the translucent header tint
const STICKY_HEADER_CELL =
  'sticky left-0 isolate bg-white-default before:absolute before:inset-0 before:-z-10 before:bg-neutral-50/20'

export type TableProps = {
  value: ContentTable
  /** `article` (blog posts): bordered cells, sticky first column and a scroll region with edge shadows */
  variant?: 'default' | 'article'
}

export function Table({ value, variant = 'default' }: TableProps) {
  const isArticle = variant === 'article'
  const { table } = value

  if (!table?.rows || table.rows.length === 0) {
    return null
  }

  const firstRow = table.rows[0]
  const hasHeaderContent = firstRow.cells?.some((cell) => cell && cell.trim() !== '')
  const shouldRenderFirstRow = hasHeaderContent

  const headerRow = shouldRenderFirstRow ? firstRow : null
  const bodyRows = shouldRenderFirstRow ? table.rows.slice(1) : table.rows.slice(1)

  return (
    <div className={ARTICLE_BLOCK_SPACING}>
      {/* Mobile: Card layout */}
      <div className="md:hidden space-y-md">
        {bodyRows.map((row) => (
          <div
            key={row._key}
            className="border border-neutral-50 bg-white-default text-white-800 p-sm"
          >
            {row.cells?.map((cell, index) => (
              <div key={index} className="mb-sm last:mb-0">
                {headerRow?.cells?.[index] && (
                  <div
                    className={cn(
                      'font-bold size-body-xs mb-2xs',
                      isArticle ? 'text-neutral-900' : 'text-neutral-700',
                    )}
                  >
                    {headerRow.cells[index]}
                  </div>
                )}
                <div className="size-body-sm" dangerouslySetInnerHTML={{ __html: cell || '' }} />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Desktop: Standard table */}
      <div
        {...(isArticle && { 'role': 'region', 'aria-label': 'Scrollable table', 'tabIndex': 0 })}
        className={cn(
          'hidden md:block overflow-x-auto',
          isArticle &&
            'article-table article-table-scroll focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current',
        )}
      >
        <table className="w-full border-collapse size-body-sm">
          {headerRow && (
            <thead
              className={cn(
                'bg-neutral-50/20',
                isArticle ? 'text-neutral-950' : 'text-neutral-700',
              )}
            >
              <tr>
                {headerRow.cells?.map((cell, index) => (
                  <th
                    key={index}
                    className={cn(
                      'border border-neutral-50 p-sm font-bold text-left',
                      !isArticle && 'md:p-md',
                      isArticle && index === 0 && STICKY_HEADER_CELL,
                    )}
                  >
                    {cell || ''}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className={cn('text-white-800', !isArticle && 'bg-white-default')}>
            {bodyRows.map((row) => (
              <tr key={row._key}>
                {row.cells?.map((cell, index) => (
                  <td
                    key={index}
                    className={cn(
                      isArticle ? 'border border-neutral-50 p-sm' : 'p-sm md:p-md',
                      isArticle && index === 0 && 'sticky left-0 bg-white-default',
                    )}
                    dangerouslySetInnerHTML={{ __html: cell || '' }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
